import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { SortingState } from '@tanstack/react-table';
import {
  useAuthUserRestaurant,
  useGetProductOfferOrdersQuery,
  useGetProductOffersQuery,
} from '@durma-soft/gros-sdk';
import {
  ProductOffer,
  ProductOfferOrder,
  ProductOfferOrdersStatusFilter,
} from '@durma-soft/gros-sdk/dist/types/product-offers';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shared/shadcn-ui/select';
import { DataTable } from '@/components/shared/data-table';
import { DatePickerWithRange } from '@/components/shared/date-picker-with-range';
import { OrderProductOfferModal } from '@/components/restaurant/product-offers/order-product-offer-modal';
import { EditProductOfferOrderModal } from '@/components/restaurant/product-offers/edit-product-offer-order-modal';

import {
  offerColumns,
  ownOrderColumns,
} from '@/config/restaurant/product-offers';
import { useDatePickerRangeState } from '@/hooks/use-date-picker-range-state';

const RestaurantProductOffers = () => {
  const { t } = useTranslation();
  const restaurant = useAuthUserRestaurant();

  const [selectedOffer, setSelectedOffer] = useState<ProductOffer | null>(null);
  const [orderToEdit, setOrderToEdit] = useState<ProductOfferOrder | null>(
    null,
  );
  const [statusFilter, setStatusFilter] =
    useState<ProductOfferOrdersStatusFilter>('active');
  const [offersSorting, setOffersSorting] = useState<SortingState>([]);
  const [ordersSorting, setOrdersSorting] = useState<SortingState>([]);

  // Defaults to today, like the other range-filtered pages.
  const { from, to, setDateRange } = useDatePickerRangeState();

  const offers = useGetProductOffersQuery({ restaurant_id: restaurant.id });
  const orders = useGetProductOfferOrdersQuery({
    restaurant_id: restaurant.id,
    status: statusFilter,
    start_date: from,
    end_date: to,
  });

  const memoizedOfferColumns = useMemo(
    () => offerColumns(t, setSelectedOffer),
    [t],
  );
  const memoizedOrderColumns = useMemo(
    () => ownOrderColumns(t, setOrderToEdit),
    [t],
  );

  return (
    <>
      <Helmet>
        <title>{t('product-offers.title')}</title>
      </Helmet>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          <h1 className="text-lg font-semibold md:text-2xl">
            {t('product-offers.title')}
          </h1>
          <DataTable
            columns={memoizedOfferColumns}
            data={offers.data || []}
            isLoading={offers.isLoading}
            sorting={offersSorting}
            setSorting={setOffersSorting}
          />
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold md:text-xl">
              {t('product-offers.my-orders')}
            </h2>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <DatePickerWithRange
                date={{ from: new Date(from), to: new Date(to) }}
                setDate={([from, to]) => {
                  setDateRange({ from, to });
                }}
              />
              <Select
                value={statusFilter}
                onValueChange={(value) =>
                  setStatusFilter(value as ProductOfferOrdersStatusFilter)
                }
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">
                    {t('product-offers.active-orders')}
                  </SelectItem>
                  <SelectItem value="closed">
                    {t('product-offers.inactive-orders')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DataTable
            columns={memoizedOrderColumns}
            data={orders.data || []}
            isLoading={orders.isLoading}
            sorting={ordersSorting}
            setSorting={setOrdersSorting}
          />
        </div>
      </div>
      {selectedOffer && (
        <OrderProductOfferModal
          offer={selectedOffer}
          onClose={() => setSelectedOffer(null)}
        />
      )}
      {orderToEdit && (
        <EditProductOfferOrderModal
          key={orderToEdit.id}
          order={orderToEdit}
          onClose={() => setOrderToEdit(null)}
        />
      )}
    </>
  );
};

export default RestaurantProductOffers;
